<script setup lang="ts">
/**
 * 「画布」页签：尺寸（viewBox）· 比例 · 背景色。
 *
 * 尺寸语义是**只动画板（viewBox），内容尺寸不变**（扩画布=留白）。
 * 比例语义是**锁**：从「无限制」起步，选一个比例才联动 —— 选了之后改一边另一边跟着变
 * （以宽为准推高；反过来也行）。锁只活在当前会话，装载文档即回「无限制」，
 * 免得打开别的图纸时被上一份的比例悄悄改尺寸。
 * 背景色两分：**视图态**（只影响编辑器显示，不写文档）与**写入文档**（`<svg style="background-color">`，导出保留）。
 */
import { computed } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { applyRatio, RATIO_PRESETS } from '@/core/ops/canvas';
import { ui } from '@/stores/editor';
import ColorField from './fields/ColorField.vue';

const editor = useEditor();

const info = computed(() => editor.canvasInfo.value);
const size = computed(() => ({
  w: Math.round(info.value?.viewBox?.w ?? info.value?.width ?? 0),
  h: Math.round(info.value?.viewBox?.h ?? info.value?.height ?? 0),
}));
const docBg = computed(() => editor.docBackground.value);
const viewBg = computed(() => ui.canvasBg ?? docBg.value ?? '#ffffff');

/**
 * 改宽/改高：**锁着比例时另一边自动跟着算**，不锁就各改各的。
 * 以「被编辑的那一边」为准 —— 所以两个方向都能用（改宽推高、改高推宽）。
 */
const onSize = (e: Event): void => {
  const input = e.target as HTMLInputElement;
  const value = Number(input.value);
  if (!Number.isFinite(value) || value < 1) return;
  const edited = input.dataset.field === 'canvas-w' ? 'width' : 'height';
  const base = {
    width: edited === 'width' ? value : size.value.w,
    height: edited === 'height' ? value : size.value.h,
  };
  const lock = ui.canvasLock;
  editor.setCanvasSize(lock === null ? base : applyRatio(base, lock, edited));
};

/** 当前锁（null = 无比例限制） */
const lock = computed(() => ui.canvasLock);
const lockLabel = computed(
  () => RATIO_PRESETS.find((preset) => preset.ratio === lock.value)?.label ?? '无限制',
);

/** 一行状态：锁着就说明「改一边另一边会跟着变」，不锁就说清「两边各自独立」 */
const lockNote = computed(() =>
  lock.value === null ? '宽高各自独立' : `${lockLabel.value} 锁定中 · 改一边另一边自动跟着变`,
);

/** 点比例＝选锁：无限制只解锁；选了具体比例就**立刻把画板调成这个比例**（以宽为准） */
const onRatio = (ratio: number | null): void => {
  ui.canvasLock = ratio;
  if (ratio !== null) editor.setCanvasRatio(ratio);
};

const onViewBg = (color: string | null): void => {
  editor.setCanvasBackground(color, 'view');
};

const toggleDocBg = (): void => {
  editor.setCanvasBackground(docBg.value ? null : (ui.canvasBg ?? '#ffffff'), 'doc');
};
</script>

<template>
  <div class="sec">
    <div class="sec-head">
      <span>画布尺寸</span>
      <span class="grow" />
      <span class="panel-note">只改画板，内容不缩放</span>
    </div>
    <div class="field">
      <label>比例</label>
      <div class="seg" data-field="canvas-ratio">
        <button
          v-for="preset in RATIO_PRESETS"
          :key="preset.label"
          :class="{ 'is-active': preset.ratio === lock }"
          :data-field="`ratio-${preset.label}`"
          :title="
            preset.ratio === null
              ? '宽高各自独立，互不影响'
              : `锁定 ${preset.label}：点一下把画布调成 ${preset.label}（以宽为准），之后改一边另一边自动跟着变`
          "
          @click="onRatio(preset.ratio)"
        >
          {{ preset.label }}
        </button>
      </div>
    </div>
    <p class="panel-note" data-field="canvas-ratio-note">{{ lockNote }}</p>
    <div class="field">
      <label>宽</label>
      <div class="input"><input data-field="canvas-w" type="number" min="1" :value="size.w" @change="onSize" /></div>
      <label>高</label>
      <div class="input"><input data-field="canvas-h" type="number" min="1" :value="size.h" @change="onSize" /></div>
    </div>
  </div>

  <div class="sec">
    <div class="sec-head">
      <span>背景</span>
      <span class="grow" />
      <span class="chip">{{ docBg ?? '未写入文档' }}</span>
    </div>
    <ColorField :value="viewBg" allow-none @change="onViewBg" />
    <div class="field">
      <span class="panel-note">仅编辑器显示</span>
      <span class="grow" />
      <button class="ghost-btn" data-field="doc-bg" @click="toggleDocBg">
        {{ docBg ? '从文档中清除' : '写入文档' }}
      </button>
    </div>
    <p class="panel-note">写入文档后，别人打开这份 SVG 也会看到底色（导出保留）</p>
  </div>

  <div class="sec">
    <div class="sec-head"><span>文档</span></div>
    <div class="seg">
      <button data-field="new-doc" @click="editor.newDocument()">新建空文档</button>
      <button @click="editor.zoomFit()">适应窗口</button>
    </div>
  </div>
</template>
